import { useMemo, useState, Suspense, lazy } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { ChangedFile } from '@aiai/shared';
import { useIsMobile } from '../hooks/useIsMobile';

// Lazy so the CodeMirror language parsers stay out of the main chunk — this
// panel sits on statically-imported pages and diffs only render on expand.
const CodeDiffView = lazy(async () => ({
  default: (await import('./CodeView')).CodeDiffView,
}));

const STATUS_META: Record<
  ChangedFile['status'],
  { letter: string; cls: string; label: string }
> = {
  added: { letter: 'A', cls: 'bg-ok-soft text-ok', label: 'added' },
  modified: { letter: 'M', cls: 'bg-warning-soft text-warning', label: 'modified' },
  deleted: { letter: 'D', cls: 'bg-bad-soft text-bad', label: 'deleted' },
  renamed: { letter: 'R', cls: 'bg-info-soft text-info', label: 'renamed' },
  copied: { letter: 'C', cls: 'bg-info-soft text-info', label: 'copied' },
  changed: { letter: '~', cls: 'bg-surface-2 text-ink-muted', label: 'changed' },
};

/** Split a unified diff into per-file sections keyed by the b/ path. */
function parseDiff(diff: string | null): Record<string, string> {
  const map: Record<string, string> = {};
  if (!diff) return map;
  const parts = diff.split(/(?=^diff --git )/m);
  for (const part of parts) {
    const m = /^diff --git a\/.+? b\/(.+)$/m.exec(part);
    if (m) map[m[1].trim()] = part.replace(/\s+$/, '');
  }
  return map;
}

/**
 * Parse a per-file unified diff section into separate original and modified
 * strings so CodeDiffView can compute and display a rich side-by-side diff.
 *
 * Only the hunk content is reconstructed (not the full file), so the viewer
 * shows the changed regions. Context lines between hunks are separated by a
 * blank line.
 */
function diffToOriginalModified(fileDiff: string): { original: string; modified: string } {
  const lines = fileDiff.split('\n');
  const originalLines: string[] = [];
  const modifiedLines: string[] = [];
  let inHunk = false;
  let firstHunk = true;

  for (const line of lines) {
    if (
      line.startsWith('diff --git') ||
      line.startsWith('index ') ||
      line.startsWith('---') ||
      line.startsWith('+++') ||
      line.startsWith('new file') ||
      line.startsWith('deleted file') ||
      line.startsWith('rename from') ||
      line.startsWith('rename to') ||
      line.startsWith('Binary')
    ) {
      continue;
    }
    if (line.startsWith('@@')) {
      if (!firstHunk) {
        originalLines.push('');
        modifiedLines.push('');
      }
      firstHunk = false;
      inHunk = true;
      continue;
    }
    if (!inHunk) continue;
    if (line === '\\ No newline at end of file') continue;

    if (line.startsWith('+')) {
      modifiedLines.push(line.slice(1));
    } else if (line.startsWith('-')) {
      originalLines.push(line.slice(1));
    } else {
      // Context line (leading space, or bare for last line edge cases)
      const ctx = line.startsWith(' ') ? line.slice(1) : line;
      originalLines.push(ctx);
      modifiedLines.push(ctx);
    }
  }

  return {
    original: originalLines.join('\n'),
    modified: modifiedLines.join('\n'),
  };
}

function DiffBody({
  fileDiff,
  filePath,
}: {
  fileDiff: string;
  filePath: string;
}) {
  const isMobile = useIsMobile();
  const { original, modified } = useMemo(
    () => diffToOriginalModified(fileDiff),
    [fileDiff],
  );

  const lineCount = Math.max(
    original.split('\n').length,
    modified.split('\n').length,
  );
  const height = Math.max(lineCount * 20 + 24, 80);

  return (
    <div className="border-t border-line">
      <Suspense
        fallback={
          <div
            style={{ height }}
            className="flex items-center justify-center text-xs text-ink-faint"
          >
            Loading editor…
          </div>
        }
      >
        <CodeDiffView
          height={height}
          path={filePath}
          original={original}
          modified={modified}
          sideBySide={!isMobile}
          lineNumbers={false}
        />
      </Suspense>
    </div>
  );
}

export function ChangesPanel({
  files,
  diff,
  defaultOpen = false,
}: {
  files: ChangedFile[];
  diff: string | null;
  /** Whether the file list starts expanded. Defaults to false (collapsed). */
  defaultOpen?: boolean;
}) {
  const byFile = useMemo(() => parseDiff(diff), [diff]);
  const [open, setOpen] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(defaultOpen);

  if (!files.length) return null;

  const totals = files.reduce(
    (acc, f) => {
      acc.add += f.additions ?? 0;
      acc.del += f.deletions ?? 0;
      return acc;
    },
    { add: 0, del: 0 },
  );

  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        onClick={() => setPanelOpen((v) => !v)}
        aria-expanded={panelOpen}
        className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-surface-2"
      >
        <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
          <span className="text-ink-faint">
            {panelOpen ? (
              <ChevronDown className="h-4 w-4" aria-hidden="true" />
            ) : (
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            )}
          </span>
          Changes{' '}
          <span className="text-xs font-normal text-ink-faint">
            {files.length} file{files.length === 1 ? '' : 's'}
          </span>
        </h2>
        <span className="font-mono text-xs">
          <span className="text-ok">+{totals.add}</span>{' '}
          <span className="text-bad">−{totals.del}</span>
        </span>
      </button>
      {!panelOpen ? null : (
      /* File rows render dynamic paths as button text — skip click tracking */
      <ul data-no-analytics className="divide-y divide-line border-t border-line">
        {files.map((f) => {
          const meta = STATUS_META[f.status] ?? STATUS_META.changed;
          const fileDiff = byFile[f.path];
          const isOpen = open === f.path;
          return (
            <li key={f.path}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : f.path)}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-surface-2"
                disabled={!fileDiff}
                title={fileDiff ? 'Show diff' : 'Diff not available yet'}
              >
                <span
                  className={`grid h-5 w-5 shrink-0 place-items-center rounded text-2xs font-bold ${meta.cls}`}
                  title={meta.label}
                >
                  {meta.letter}
                </span>
                <span className="min-w-0 flex-1 truncate font-mono text-xs text-ink">
                  {f.path}
                </span>
                <span className="shrink-0 font-mono text-2xs">
                  {f.additions != null && (
                    <span className="text-ok">+{f.additions}</span>
                  )}{' '}
                  {f.deletions != null && (
                    <span className="text-bad">−{f.deletions}</span>
                  )}
                </span>
                {fileDiff && (
                  <span className="shrink-0 text-ink-faint">
                    {isOpen ? (
                      <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                  </span>
                )}
              </button>
              {isOpen && fileDiff && (
                <DiffBody fileDiff={fileDiff} filePath={f.path} />
              )}
            </li>
          );
        })}
      </ul>
      )}
    </div>
  );
}
